import React, { useState } from "react";
import { Button } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import * as XLSX from "xlsx";
import { useAuth } from "../AuthProvider";
import { useMsal } from "@azure/msal-react";
import urlconfig from "../config/urlconfig";
import { getApiToken } from "../authUtils";
import { formatCurrency } from "../util";

const ExportAlldata = () => {
  const { account } = useAuth();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);

  const fetchPage = async (page, size = 10000) => {
    try {
      const accessToken = await getApiToken(instance, account);

      const response = await fetch(
        `${urlconfig.backend_api_base_url}/api/v2/items?page=${page}&size=${size}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch items");
      return await response.json();
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const fetchAllPages = async () => {
    let allItems = [];
    let page = 0;
    let totalPages = 1;

    setLoading(true);

    while (page < totalPages) {
      const data = await fetchPage(page, 500);
      if (!data) break;

      allItems.push(...(data.content || []));
      totalPages = data.totalPages || 1;
      page++;
      await new Promise((res) => setTimeout(res, 50));
    }

    setLoading(false);
    return allItems;
  };

  const exportAllData = async () => {
    const items = await fetchAllPages();

    if (!items || items.length === 0) {
      alert("No data to export");
      return;
    }

    const excelRows = items.map((item) => {
      // helper sums for items
      const spWithGst = item.items.reduce(
        (acc, x) => acc + x.qty * (x.sp * (1 + (x.gst || 0) / 100)),
        0
      );
      const ppWithGst = item.items.reduce(
        (acc, x) => acc + x.qty * (x.pp * (1 + (x.gst || 0) / 100)),
        0
      );

      const ppWithoutGST = item.items.reduce(
        (acc, x) => acc + x.qty * x.pp,
        0
      );

      const spWithoutGST = item.items.reduce(
        (acc, x) => acc + x.qty * x.sp,
        0
      );


      // transportation / customization / marketing totals (may be undefined)
      const tPp = Number(item.transportationTotalPp || 0);
      const tSp = Number(item.transportationTotalSp || 0);
      const cPp = Number(item.customizationTotalPp || 0);
      const cSp = Number(item.customizationTotalSp || 0);
      const mPp = Number(item.marketingTotalPp || 0);
      const mSp = Number(item.marketingTotalSp || 0);
      const customPP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
      const customSP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);

      const customPPWithGst = (item.customRows || []).reduce((s, r) => {
        const pp = parseFloat(r.customTotalPp) || 0;
        const gst = parseFloat(r.customGST) || 0;
        return s + pp * (1 + gst / 100);
      }, 0);
      const customSPWithGst = (item.customRows || []).reduce((s, r) => {
        const sp = parseFloat(r.customTotalSp) || 0;
        const gst = parseFloat(r.customGST) || 0;
        return s + sp * (1 + gst / 100);
      }, 0);

      // Totals including extras
      const tGst = Number(item.costSummary?.transportationGST || 0);
      const cGst = Number(item.costSummary?.customizationGST  || 0);
      const mGst = Number(item.costSummary?.marketingGST      || 0);
      const totalPp = ppWithGst + tPp * (1 + tGst / 100) + cPp * (1 + cGst / 100) + mPp * (1 + mGst / 100) + customPPWithGst;
      const totalSp = spWithGst + tSp * (1 + tGst / 100) + cSp * (1 + cGst / 100) + mSp * (1 + mGst / 100) + customSPWithGst;


      const totalPPExclGST = ppWithoutGST + tPp + cPp + mPp + customPP ;
      const totalSPExclGST = spWithoutGST + tSp + cSp + mSp + customSP;

      const marginVal      = totalSPExclGST - totalPPExclGST;

      const marginValExclGST =
        spWithoutGST - ppWithoutGST +
        (tSp - tPp) + (cSp - cPp) + (mSp - mPp) ;

      const poNumbers = (item.items || []).map((i) => i.poNumber).join(", ");
      const details = (item.items || []).map((i) => i.details).join(", ");
      // Same order as Details; a line without custom details shows "-" so the two lists line up.
      // Lines are split with " | " because custom details often contain commas
      const customDetails = (item.items || []).some((i) => i.customDetails?.trim())
        ? (item.items || []).map((i) => i.customDetails?.trim() || "-").join(" | ")
        : "";
      const brands = (item.items || []).map((i) => i.brand?.name || "N/A").join(", ");
      const categories = (item.items || []).map((i) => i.category?.name || "N/A").join(", ");
      const subcategories = (item.items || []).map((i) => i.subcategory?.name || "N/A").join(", ");

      return {
        Date: item.date ? new Date(item.date).toLocaleDateString("en-IN") : "",
        "Creation Date": item.createdAT ? new Date(item.createdAT).toLocaleString("en-IN") : "N/A",
        "Cost Sheet For": item.costsheetfor || "",
        "Client Name": item.clientName || "",
        "Sales Order No": item.salesOrderNo || "",
        "SCM SPOC": item.scmspoc || "",
        "Sales SPOC": item.salesPOC || "",
        Details: details,
        "Custom Details": customDetails,
        Brand: brands,
        Category: categories,
        Subcategory: subcategories,
        "Delivery Date": item.deliveryDate ? new Date(item.deliveryDate).toLocaleDateString("en-IN") : "",
        Margin: formatCurrency(marginVal),
        "Total PP (Incl GST)": formatCurrency(totalPp),
        "Total SP (Incl GST)": formatCurrency(totalSp),
        "Total PP (Excl GST)": formatCurrency(totalPPExclGST),
        "Total SP (Excl GST)": formatCurrency(totalSPExclGST),
        "Transportation PP": formatCurrency(tPp),
        "Transportation SP": formatCurrency(tSp),
        "Customization PP": formatCurrency(cPp),
        "Customization SP": formatCurrency(cSp),
        "Marketing PP": formatCurrency(mPp),
        "Marketing SP": formatCurrency(mSp),
        "Margin (Excl GST)": formatCurrency(marginValExclGST),
        "PO Number": poNumbers,
        "Created By": item.createdBy || "",
        "Approval Status":
          item.pending === "true"
            ? `Approved By ${item.approvedBy?.join(", ")}`
            : item.pending === "false"
              ? `Rejected: ${item.approvedBy?.join(", ")} — Note: ${item.remarks}`
              : "Pending",
        "Order Status": item.orderStatus || "",
      };
    });

    // Line items wise export data
    const lineItemRows = [];
    items.forEach(item => {
      const spWithGst = item.items.reduce(
        (acc, x) => acc + x.qty * (x.sp * (1 + (x.gst || 0)/100)),
        0
      );
      const ppWithGst = item.items.reduce(
        (acc, x) => acc + x.qty * (x.pp * (1 + (x.gst || 0)/100)),
        0
      );

      const tPp = Number(item.transportationTotalPp || 0);
      const tSp = Number(item.transportationTotalSp || 0);
      const cPp = Number(item.customizationTotalPp || 0);
      const cSp = Number(item.customizationTotalSp || 0);
      const mPp = Number(item.marketingTotalPp || 0);
      const mSp = Number(item.marketingTotalSp || 0);


      const ppWithoutGST = item.items.reduce(
        (acc, x) => acc + x.qty * x.pp,
        0
      );

      const spWithoutGST = item.items.reduce(
        (acc, x) => acc + x.qty * x.sp,
        0
      );

      const customPP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
      const customSP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
      const totalPPExclGST = ppWithoutGST + tPp + cPp + mPp + customPP;
      const totalSPExclGST = spWithoutGST + tSp + cSp + mSp + customSP;

      const marginValExclGST = totalSPExclGST - totalPPExclGST;

      const tGst = Number(item.costSummary?.transportationGST || 0);
      const cGst = Number(item.costSummary?.customizationGST  || 0);
      const mGst = Number(item.costSummary?.marketingGST      || 0);
      const customPPWithGst = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0) * (1 + (parseFloat(r.customGST) || 0) / 100), 0);
      const customSPWithGst = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0) * (1 + (parseFloat(r.customGST) || 0) / 100), 0);
      const totalPPInclGST = ppWithGst + tPp * (1 + tGst / 100) + cPp * (1 + cGst / 100) + mPp * (1 + mGst / 100) + customPPWithGst;
      const totalSPInclGST = spWithGst + tSp * (1 + tGst / 100) + cSp * (1 + cGst / 100) + mSp * (1 + mGst / 100) + customSPWithGst;




      item.items.forEach((line, index) => {
        lineItemRows.push({
          // Only show order-level info in the first line of the order
          Date: index === 0 ? (item.date ? new Date(item.date).toLocaleDateString("en-IN") : "") : "",
          "Creation Date": index === 0 ? (item.createdAT ? new Date(item.createdAT).toLocaleString("en-IN") : "N/A") : "",
          "Cost Sheet For": index === 0 ? item.costsheetfor || "" : "",
          "Client Name": index === 0 ? item.clientName || "" : "",
          "Sales Order No": index === 0 ? item.salesOrderNo || "" : "",
          "SCM SPOC": index === 0 ? item.scmspoc || "" : "",
          "Sales SPOC": index === 0 ? item.salesPOC || "" : "",
          "Created By": index === 0 ? item.createdBy || "" : "",
          "Approval Status": index === 0
            ? (item.pending === "true"
              ? `Approved By ${item.approvedBy?.join(", ")}`
              : item.pending === "false"
                ? `Rejected: ${item.approvedBy?.join(", ")} — Note: ${item.remarks}`
                : "Pending")
            : "",
          "Order Status": index === 0 ? item.orderStatus || "" : "",

          // Line item specific columns
          Details: line.details || "",
          "Custom Details": line.customDetails || "",
          Brand: line.brand?.name || "N/A",
          Category: line.category?.name || "N/A",
          Subcategory: line.subcategory?.name || "N/A",
          Quantity: line.qty,
          PP: formatCurrency(line.pp),
          "PP Incl GST": formatCurrency(line.pp * (1 + (line.gst || 0) / 100)),
          SP: formatCurrency(line.sp),
          "SP Incl GST": formatCurrency(line.sp * (1 + (line.gst || 0) / 100)),
          GST: `${line.gst || 0}%`,
          "PO Number": line.poNumber || "",

          // Totals and margin only on first line
          Margin: index === 0 ? formatCurrency(marginValExclGST) : "",
          "Total PP (Incl GST)": index === 0 ? formatCurrency(totalPPInclGST) : "",
          "Total SP (Incl GST)": index === 0 ? formatCurrency(totalSPInclGST) : "",

          "Total PP (Excl GST)": index === 0 ? formatCurrency(totalPPExclGST) : "",
          "Total SP (Excl GST)": index === 0 ? formatCurrency(totalSPExclGST) : "",
          "Margin (Excl GST) ": index=== 0 ? formatCurrency(marginValExclGST) : "",
          "Transportation PP": index === 0 ? formatCurrency(tPp) : "",
          "Transportation SP": index === 0 ? formatCurrency(tSp) : "",
          "Customization PP": index === 0 ? formatCurrency(cPp) : "",
          "Customization SP": index === 0 ? formatCurrency(cSp) : "",
          "Marketing PP": index === 0 ? formatCurrency(mPp) : "",
          "Marketing SP": index === 0 ? formatCurrency(mSp) : "",
        });
      });
    });




    const worksheet = XLSX.utils.json_to_sheet(excelRows);
    const lineItemSheet = XLSX.utils.json_to_sheet(lineItemRows);

    Object.keys(worksheet).forEach((cell) => {
      if (cell.startsWith("!")) return;
      worksheet[cell].s = {
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        },
      };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All_Cost_Sheets");
    XLSX.utils.book_append_sheet(workbook, lineItemSheet, "Line_items");

    // Write file
    XLSX.writeFile(workbook, "All_Cost_sheet_data.xlsx");
  };

  return (
    <Button
      onClick={exportAllData}
      variant="contained"
      color="primary"
      disabled={loading}
    >
      <FileDownloadOutlinedIcon sx={{ mr: 1 }} />
      {loading ? "Exporting..." : "Export All Data"}
    </Button>
  );
};

export default ExportAlldata;
